import { useMemo, useState } from 'react';
import {
  METAFIELD_ENTITIES,
  METAFIELD_TYPES,
  isValidMetafieldKey,
  metafieldKeyFrom,
  type Metafield,
  type MetafieldEntity,
  type MetafieldType,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  CheckboxField,
  Dialog,
  Icon,
  Select,
  TextInput,
  type MenuItem,
} from '@forecastify/ui';
import { useI18n, useT } from '../../i18n';
import { usePageHeader } from '../page-header';
import { SettingsPage } from '../settings/settings-page';
import { SettingsTable, type SettingsRow } from '../settings/settings-table';
import { Field } from '../card';
import { entityLabel, typeLabel } from './metafield-form';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Metafieldek admin — one tab per entity, CRUD, reorder, duplicate.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **This screen is C10's, and C16 owns the settings SHELL it will eventually sit in.** Its row
 * in `chunks.md` lists *"general, statuses and priorities, worklog rules, data, billing"* — the
 * metafields admin is C10's own scope. When C16 lands it hosts this screen as a section rather than
 * rewriting it; the address changes and nothing else does.
 *
 * ⚠️ **Reorder is drag AND a menu, for the reason the board records:** no key event starts an HTML5
 * drag, so drag-only reordering is unusable without a pointer. *Feljebb* and *Lejjebb* in the row
 * menu perform the identical reorder.
 */
export interface MetafieldDraft {
  entity: MetafieldEntity;
  name: string;
  key: string;
  /** Whether the key has been typed into — once it has, the name stops driving it. */
  keyTouched: boolean;
  type: MetafieldType;
  required: boolean;
  inList: boolean;
  help: string;
  options: string[];
  optionDraft: string;
}

const TYPES: readonly MetafieldType[] = [
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'multi',
  'checkbox',
  'user',
  'url',
];

export function MetafieldsScreen({
  loading,
  fields,
  canManage,
  onCreate,
  onUpdate,
  onDuplicate,
  onReorder,
  onDelete,
  serverError,
  entity,
  onSelect,
}: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly fields: readonly Metafield[];
  readonly canManage: boolean;
  /** 26 Oct 2026 · resolve to whether the save landed; the dialog closes on `true` only. */
  readonly onCreate: (draft: MetafieldDraft) => Promise<boolean>;
  readonly onUpdate: (field: Metafield, draft: MetafieldDraft) => Promise<boolean>;
  readonly onDuplicate: (field: Metafield) => void;
  readonly onReorder: (entity: MetafieldEntity, ids: readonly string[]) => void;
  readonly onDelete: (field: Metafield) => void;
  readonly serverError: string | null;
  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ TWO STEPS, AND THE STEP IS AN ADDRESS — round 5 item 3.                 │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * `undefined` is the LIST of entity types; a value is that type's fields. The screen was seven
   * tabs across the top — *"a bit too wide with how many tabs we have"* — and the product owner
   * named the shape to copy: *Jogosultságok*, which is a table of levels that opens one.
   *
   * ⚠️⚠️ **AN ADDRESS RATHER THAN COMPONENT STATE, WHICH IS R-8 AND WAS ASKED FOR BY NAME.**
   * `permissions-screen.tsx` made exactly this move for exactly this reason: a thing somebody is
   * arguing about over chat should be a link, and the browser's Back should be the way out.
   */
  readonly entity?: MetafieldEntity;
  /** Opens one type. Absent where the reader cannot navigate — the rows are then not buttons. */
  readonly onSelect?: (entity: MetafieldEntity) => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [editing, setEditing] = useState<Metafield | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [doomed, setDoomed] = useState<Metafield | null>(null);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const shown = useMemo(
    () => (entity === undefined ? [] : fields.filter((field) => field.entity === entity)),
    [fields, entity],
  );

  /* ⚠️ The search and the type filter, applied here: `DataList` filters nothing on its own. */
  const shownFiltered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return shown.filter(
      (one) =>
        (needle === '' ||
          one.name.toLowerCase().includes(needle) ||
          one.key.toLowerCase().includes(needle)) &&
        (typeFilter === '' || one.type === typeFilter),
    );
  }, [shown, query, typeFilter]);

  function fieldMenu(id: string, index: number): MenuItem[] {
    const field = shown.find((one) => one.id === id);
    if (field === undefined) return [];
    return [
      {
        label: t('common.edit'),
        icon: 'pencil',
        onSelect: () => {
          setEditing(field);
          setDialogOpen(true);
        },
      },
      {
        label: t('metafields.duplicate'),
        icon: 'copy',
        onSelect: () => {
          onDuplicate(field);
        },
      },
      { type: 'separator' },
      /* ⚠️ The only path for reordering now that the grabber is gone — see the list. */
      {
        label: t('list.moveUp', { label: field.name }),
        icon: 'chevron-up',
        disabled: index === 0,
        onSelect: () => {
          nudge(field, -1);
        },
      },
      {
        label: t('list.moveDown', { label: field.name }),
        icon: 'chevron-down',
        disabled: index === shown.length - 1,
        onSelect: () => {
          nudge(field, 1);
        },
      },
      { type: 'separator' },
      {
        label: t('metafields.delete'),
        icon: 'trash',
        destructive: true,
        onSelect: () => {
          setDoomed(field);
        },
      },
    ];
  }

  /** Move a field one place, through the same callback a drag uses. */
  const nudge = (field: Metafield, by: -1 | 1): void => {
    /* ⚠️ Only reachable from a row, and a row only exists on the second step. */
    if (entity === undefined) return;
    const ids = shown.map((candidate) => candidate.id);
    const from = ids.indexOf(field.id);
    const to = from + by;
    if (from < 0 || to < 0 || to >= ids.length) return;

    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, field.id);
    onReorder(entity, next);
  };

  /**
   * ⚠️ **The heading and the CTA belong to the SHELL'S bar.** Every other settings section leaves
   * the chrome's 48px header alone and it renders the route's own title; this one drew a second one
   * beneath it, so the screen opened with *Metamezők* twice.
   *
   * ⚠️ **The lede is gone rather than relocated.** It restated what the entity tabs and the field
   * dialog already say at the point of use, and a paragraph above the content is a line every
   * reader steps over every visit.
   */
  /**
   * ⚠️⚠️ **THE CREATE SITS ON THE TAB ROW, NOT IN THE HEADER — item 25, 15 Sep.** Every list
   * screen moved its create into the toolbar beside the search; this screen has no toolbar, so it
   * goes on the one row it does have. What matters is the rule behind the ask: the act of adding
   * belongs on the same line as the controls for what is already there, and it is an outline
   * button rather than a purple one.
   */
  /* ⚠️ No shell header: the page names itself now, and two titles one above the other is what
     made this screen look unlike every other settings screen. */
  usePageHeader(() => null, []);

  const entityRows: SettingsRow[] = METAFIELD_ENTITIES.map((candidate) => ({
    id: candidate,
    onOpen: () => {
      onSelect?.(candidate);
    },
    cells: [
      { kind: 'name', text: entityLabel(t, candidate) },
      /* ⚠️ The chevron says the row opens. It is not a second target — the row itself is the
         control, which is what `SettingsTable` gives a row with an `onOpen`. */
      { kind: 'chevron' },
    ],
  }));

  const fieldRows: SettingsRow[] = shownFiltered.map((field, index) => ({
    id: field.id,
    onOpen: !canManage
      ? undefined
      : () => {
          setEditing(field);
          setDialogOpen(true);
        },
    cells: [
      { kind: 'blank' },
      /* ⚠️ The key in MONO under the name — it is a machine identifier, and the design system's
         rule is to show one as it is rather than prettify it. */
      { kind: 'name', text: field.name, monoSub: field.key },
      { kind: 'badge', text: typeLabel(t, field.type), tone: 'outline' },
      {
        kind: 'badges',
        items: [
          ...(field.required ? [{ label: t('metafields.required') }] : []),
          ...(field.inList ? [{ label: t('metafields.inList'), tone: 'outline' as const }] : []),
        ],
      },
      !canManage
        ? { kind: 'blank' }
        : {
            kind: 'kebab',
            ariaLabel: t('list.rowMenu'),
            items: fieldMenu(field.id, index),
          },
    ],
  }));

  return (
    /**
     * ⚠️⚠️ **A MARKER PER STEP, BECAUSE THE ROUTE GATE REQUIRES ONE PER ADDRESS.** One component
     * renders both, and `routes.test.tsx` asserts each address renders its OWN screen — two
     * addresses sharing a marker is how a route silently resolves to its neighbour with the check
     * still green.
     */
    <div
      data-screen={entity === undefined ? 'metafields' : 'metafield-entity'}
      className="flex min-h-0 flex-1 flex-col"
    >
      <SettingsPage
        /* ⚠️⚠️ **THE TYPE LIST IS AN *INDEX*, NOT A COLLECTION.** The mockup centres a drill
           index on 760px and gives it a card of 46px rows with no column heads — it is a menu of
           things to open, and a header strip over one column names nothing. */
        width={entity === undefined ? 'index' : 'full'}
        /* ⚠️⚠️ **`metafields.heading`, NOT `metafields.entities`.** The first is *Metafieldek* —
           the section's name, which is what a settings page's title is. The second is *Entitások*,
           which named the LIST rather than the screen and was reported three times. */
        title={entity === undefined ? t('metafields.heading') : entityLabel(t, entity)}
        action={
          !canManage || entity === undefined
            ? undefined
            : {
                label: t('metafields.create', {
                  entity: entityLabel(t, entity).toLocaleLowerCase(locale),
                }),
                icon: 'plus',
                onSelect: () => {
                  setEditing(null);
                  setDialogOpen(true);
                },
              }
        }
        error={serverError}
        search={entity === undefined ? undefined : query}
        onSearch={entity === undefined ? undefined : setQuery}
        searchPlaceholder={t('metafields.search')}
        filters={
          entity === undefined
            ? []
            : [
                {
                  key: 'type',
                  value: typeFilter,
                  ariaLabel: t('metafields.column.type'),
                  options: [
                    { value: '', label: t('metafields.allTypes') },
                    ...METAFIELD_TYPES.map((one) => ({ value: one, label: typeLabel(t, one) })),
                  ],
                  onChange: setTypeFilter,
                },
              ]
        }
      >
        {entity === undefined ? (
          /* ⚠️ Every entity, including the empty ones: the row is where somebody goes to add the
             FIRST field, so a type with none has to be listed or the feature has no entrance. */
          <SettingsTable
            grid="minmax(0,1fr) 36px"
            minWidth={0}
            columns={[{}, {}]}
            rowHeight={46}
            groups={[{ rows: entityRows }]}
            loading={loading}
            empty={{ icon: 'tag', title: t('metafields.heading'), body: '' }}
          />
        ) : (
          <SettingsTable
            grid="26px minmax(0,1fr) 140px 176px 36px"
            minWidth={760}
            columns={[
              {},
              { label: t('metafields.column.name') },
              { label: t('metafields.column.type') },
              {},
              {},
            ]}
            groups={[{ rows: fieldRows }]}
            loading={loading}
            empty={{
              icon: 'tag',
              title: t('metafields.empty', { entity: entityLabel(t, entity) }),
              body: t('metafields.emptyBody'),
            }}
          />
        )}
      </SettingsPage>

      {/* ⚠️⚠️ **ONLY ON THE SECOND STEP, BECAUSE THE DIALOG NEEDS A TYPE TO CREATE INTO.** The
          entity left the form in round 4 — it always opened with the tab's value chosen and then
          asked again — so the screen supplies it, and on the type LIST there is no answer. The
          create button is absent there for the same reason, which is why this cannot be reached. */}
      {entity === undefined ? null : (
        <MetafieldDialog
          open={dialogOpen}
          editing={editing}
          entity={entity}
          serverError={serverError}
          onClose={() => {
            setDialogOpen(false);
          }}
          onSubmit={(draft) => {
            /* 26 Oct 2026 · closed only once it lands; a refusal stays in the open dialog. */
            void (editing === null ? onCreate(draft) : onUpdate(editing, draft)).then((landed) => {
              if (landed) setDialogOpen(false);
            });
          }}
        />
      )}

      <DeleteDialog
        field={doomed}
        onClose={() => {
          setDoomed(null);
        }}
        onConfirm={(field) => {
          onDelete(field);
          setDoomed(null);
        }}
      />
    </div>
  );
}

/* ── the create / edit dialog ───────────────────────────────────────────────────────────────── */

function MetafieldDialog({
  open,
  editing,
  entity,
  onClose,
  onSubmit,
  serverError,
}: {
  readonly open: boolean;
  readonly editing: Metafield | null;
  readonly entity: MetafieldEntity;
  readonly onClose: () => void;
  readonly onSubmit: (draft: MetafieldDraft) => void;
  readonly serverError: string | null;
}) {
  const t = useT();
  /* ⚠️ The entity name is lower-cased INSIDE a sentence, and Hungarian and English disagree about
     which letters have a lower case — `toLocaleLowerCase` takes the locale rather than assuming. */
  const { locale } = useI18n();
  const [draft, setDraft] = useState<MetafieldDraft>(() => empty(entity));
  const [error, setError] = useState<string | null>(null);
  const [openedFor, setOpenedFor] = useState<string | null>(null);

  /**
   * ⚠️ **Reset when the dialog OPENS, keyed on which field it opened for.** A `useEffect` on `open`
   * would also fire on every re-render while open — and this component re-renders on every
   * keystroke, so the draft would be wiped as it was typed. The key is the field's id, or `'new'`.
   */
  const openedKey = open ? (editing?.id ?? 'new') : null;
  if (openedKey !== openedFor) {
    setOpenedFor(openedKey);
    setError(null);
    setDraft(
      editing === null
        ? empty(entity)
        : {
            entity: editing.entity,
            name: editing.name,
            key: editing.key,
            keyTouched: true,
            type: editing.type,
            required: editing.required,
            inList: editing.inList,
            help: editing.help ?? '',
            options: [...editing.options],
            optionDraft: '',
          },
    );
  }

  const patch = (part: Partial<MetafieldDraft>): void => {
    setDraft((current) => ({ ...current, ...part }));
    setError(null);
  };

  const isChoice = draft.type === 'select' || draft.type === 'multi';

  const addOption = (): void => {
    const value = draft.optionDraft.trim();
    if (value === '') return;
    /* Case-insensitive, matching the mockup: two options differing only in case are one option
     * as far as anybody reading the list is concerned. */
    if (draft.options.some((option) => option.toLowerCase() === value.toLowerCase())) {
      setError(t('metafields.error.optionExists'));
      return;
    }
    patch({ options: [...draft.options, value], optionDraft: '' });
  };

  const submit = (): void => {
    if (draft.name.trim() === '') {
      setError(t('metafields.error.name'));
      return;
    }
    if (!isValidMetafieldKey(draft.key)) {
      setError(t('metafields.error.key'));
      return;
    }
    if (isChoice && draft.options.length === 0) {
      setError(t('metafields.error.noOptions'));
      return;
    }
    onSubmit(draft);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        editing === null
          ? t('metafields.dialog.new', { entity: entityLabel(t, entity).toLocaleLowerCase(locale) })
          : t('metafields.dialog.edit')
      }
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field required label={t('metafields.field.name')}>
            <TextInput
              value={draft.name}
              autoFocus
              onChange={(event) => {
                /**
                 * ⚠️⚠️ **The key follows the name UNTIL the key is typed into**, which is the
                 * mockup's `keyTouched` and the right behaviour: it saves typing for the common
                 * case and stops overwriting a key somebody deliberately chose.
                 */
                const name = event.target.value;
                patch({
                  name,
                  ...(draft.keyTouched ? {} : { key: metafieldKeyFrom(name) }),
                });
              }}
            />
          </Field>

          <Field
            required
            label={t('metafields.field.key')}
            note={editing === null ? undefined : t('metafields.field.keyWarning')}
          >
            <TextInput
              value={draft.key}
              onChange={(event) => {
                /* Same filter as the mockup's: anything else becomes an underscore as you type,
                 * so the field cannot hold a value the column would refuse. */
                patch({
                  key: event.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9_]/g, '_')
                    .slice(0, 40),
                  keyTouched: true,
                });
              }}
            />
          </Field>

          {/**
           * ┌───────────────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ THE ENTITY IS THE TAB YOU ARE ON — 15 Sep. There is no picker.       │
           * └───────────────────────────────────────────────────────────────────────────┘
           *
           * *"We need a context-aware add button: depending on what tab you're on is what metafield
           * you're adding, not selected inside the add field."*
           *
           * The dialog always opened with the tab's entity already chosen and then asked again — a
           * field whose only correct answer was the one it already held, and whose wrong answers put
           * the new row on a tab the person was not looking at. It was locked on edit for a real
           * reason (a field cannot move between entities: its values point at rows of one kind) and
           * that reason applies just as well to a create made from a tab.
           *
           * ⚠️ **The entity still travels in `draft`** — the create request needs it — so nothing
           * about the write changed. What went is the question.
           */}

          <Field
            label={t('metafields.field.type')}
            {...(editing !== null && editing.usageCount > 0
              ? { note: t('metafields.field.typeWarning', { count: editing.usageCount }) }
              : {})}
          >
            <Select
              value={draft.type}
              onChange={(event) => {
                const type = event.target.value as MetafieldType;
                /* ⚠️ Options are dropped when leaving a choice type, matching the column's check —
                 * options on a text field would reappear if somebody changed it back. */
                patch({
                  type,
                  options: type === 'select' || type === 'multi' ? draft.options : [],
                });
              }}
              options={TYPES.map((type) => ({ value: type, label: typeLabel(t, type) }))}
            />
          </Field>
        </div>

        <Field label={t('metafields.field.help')}>
          <TextInput
            value={draft.help}
            onChange={(event) => {
              patch({ help: event.target.value });
            }}
          />
        </Field>

        <div className="flex flex-wrap gap-4">
          <CheckboxField
            checked={draft.required}
            label={t('metafields.field.required')}
            onCheckedChange={(next) => {
              patch({ required: next });
            }}
          />
          <CheckboxField
            checked={draft.inList}
            label={t('metafields.field.inList')}
            onCheckedChange={(next) => {
              patch({ inList: next });
            }}
          />
        </div>

        {isChoice ? (
          <div className="flex flex-col gap-2">
            <span className="text-[12px] font-medium">{t('metafields.field.options')}</span>

            <div className="flex flex-wrap gap-1.5">
              {draft.options.map((option) => (
                <Badge key={option} variant="secondary">
                  {option}
                  <button
                    type="button"
                    className="ml-1"
                    aria-label={t('metafields.removeOption', { value: option })}
                    onClick={() => {
                      patch({ options: draft.options.filter((entry) => entry !== option) });
                    }}
                  >
                    <Icon name="x" size={11} />
                  </button>
                </Badge>
              ))}
            </div>

            <div className="flex gap-2">
              <TextInput
                value={draft.optionDraft}
                placeholder={t('metafields.field.optionPlaceholder')}
                aria-label={t('metafields.field.options')}
                onChange={(event) => {
                  patch({ optionDraft: event.target.value });
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addOption();
                  }
                }}
              />
              <Button variant="outline" onClick={addOption}>
                {t('metafields.addOption')}
              </Button>
            </div>
          </div>
        ) : null}

        {error === null && serverError === null ? null : (
          <p className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </p>
        )}

        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit}>
            {/**
             * ⚠️ The dialog's own title already names the entity, so the button that commits it is
             * a plain verb rather than the CTA's sentence.
             *
             * ⚠️⚠️ **`create`, NOT `add` — the option editor below already owns *Hozzáadás*.** Two
             * buttons with one label inside one dialog is ambiguous to a screen reader and to a
             * test, and the test is how this was caught.
             */}
            {editing === null ? t('common.create') : t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * ⚠️⚠️ **THE DELETE DIALOG STATES THE COUNT, which is the whole reason `usageCount` exists.** The
 * mockup's own sentence: *"128 feladaton van kitöltve érték. A törléssel ezek is eltűnnek, és a
 * kulcs (`szamlazhato`) kiesik az exportokból."* A confirm dialog that cannot name the number is
 * one nobody can answer.
 *
 * ⚠️ **A neutral outline button, not the filled destructive one.** `design-system/readme.md`
 * reserves the filled danger button for irreversible financial deletions; this is a settings change,
 * and making it the loudest thing on the dialog would spend that emphasis where it is not due.
 */
function DeleteDialog({
  field,
  onClose,
  onConfirm,
}: {
  readonly field: Metafield | null;
  readonly onClose: () => void;
  readonly onConfirm: (field: Metafield) => void;
}) {
  const t = useT();

  return (
    <Dialog
      open={field !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('metafields.delete.title')}
      closeLabel={t('common.close')}
    >
      {field === null ? null : (
        <div className="flex flex-col gap-3">
          <p className="text-[12.5px] leading-relaxed">
            {field.usageCount === 0
              ? t('metafields.delete.unused', { name: field.name })
              : t('metafields.delete.used', {
                  name: field.name,
                  count: field.usageCount,
                  key: field.key,
                })}
          </p>

          <div className="flex items-center gap-2">
            <div className="flex-1" />
            <Button variant="outline" size="lg" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="text-destructive"
              onClick={() => {
                onConfirm(field);
              }}
            >
              {t('metafields.delete')}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function empty(entity: MetafieldEntity): MetafieldDraft {
  return {
    entity,
    name: '',
    key: '',
    keyTouched: false,
    type: 'text',
    required: false,
    inList: false,
    help: '',
    options: [],
    optionDraft: '',
  };
}
